import type { ReactNode } from 'react';
import type { FeedPost } from '@wlb/shared/types/post';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { StatusPill } from '../../components/ui/StatusPill';
import { formatDateTime } from '../../utils/format';
import { resolveImageUrl } from '../../utils/resolveImageUrl';
import { PostCard, type PostCardFact } from './PostCard';
import { postPlainText, resolvePostImages, resolvePostVideo } from './postMedia';

interface AdminPostCardProps {
  post: FeedPost;
  selection?: { selected: boolean; onToggle: () => void };
  /** Extra operator-only facts, e.g. why the post is in this queue. */
  facts?: readonly PostCardFact[];
  factsTone?: 'neutral' | 'danger';
  actions?: ReactNode;
}

/**
 * Adapts a `FeedPost` to the shared post card.
 *
 * Every admin surface that lists member posts goes through here, so the panel
 * cannot drift into two different ideas of what a post looks like.
 */
export function AdminPostCard({
  post,
  selection,
  facts,
  factsTone,
  actions,
}: AdminPostCardProps) {
  const { t } = useTranslation('admin');
  const authorName = post.username || t('common.none');
  const typeLabel = post.isBlog
    ? t('posts.type.blog')
    : post.groupName
      ? t('posts.type.group')
      : t('posts.type.post');

  return (
    <PostCard
      authorName={authorName}
      authorAvatar={resolveImageUrl(post.avatar)}
      meta={[
        formatDateTime(post.postDate) || post.timeAgo,
        post.groupName,
        post.currentWeight ? t('posts.card.currentWeight', { weight: post.currentWeight }) : null,
      ]}
      badges={<StatusPill label={typeLabel} tone={post.isBlog ? 'neutral' : 'info'} />}
      title={post.title}
      text={postPlainText(post.text || post.content) || t('posts.card.noContent')}
      images={resolvePostImages(post)}
      videoUrl={resolvePostVideo(post)}
      engagement={{ likes: post.likes, comments: post.comments, shares: post.shares }}
      facts={facts}
      factsTone={factsTone}
      selection={selection}
      actions={actions}
    />
  );
}
